import { Skeleton } from "@mui/material";
import { ConditionOperator as ConditionOperatorEnum } from "@sinch/core/entities/serverEnums";
import { head, mergeDeepRight } from "ramda";
import { ensureArray, isArray, isNotNil, isNotNilOrEmpty, isNull } from "ramda-adjunct";
import React, { forwardRef, type Ref, Suspense, useCallback, useMemo } from "react";
import type { DeepPartial } from "react-hook-form";
import { useOperator } from "@sinch/components/application/ConditionBuilder/useOperator";
import {
  FullWidthGrid,
  OperatorGridItem,
  SubjectGridItem,
  ValueGridItem,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/components/styled";
import type { ConditionInputValue } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import {
  ConditionOperatorSelect,
  ConditionOperatorSelectProps,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionOperatorSelect";
import {
  ConditionSubjectSelect,
  ConditionSubjectSelectProps,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/ConditionSubjectSelect";
import { useSelectedRateConditionSubject } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/hooks";
import {
  ConditionValueInput,
  ConditionValueInputProps,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValueInput";

/**
 * Properties for the ConditionInputInputsProps interface, providing configuration
 * and event handlers for condition input components.
 */
interface ConditionInputInputsProps {
  onFocusChange: (index: number) => void;
  onChangeValue: (val: ConditionInputValue | null) => void;
  value: ConditionInputValue | null;
  empty?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
}

/**
 * Represents the default input values for a condition input.
 */
const conditionInputDefault: ConditionInputValue = {
  category: null,
  subjectId: null,
  type: null,
  operator: null,
  value: null,
};

export const ConditionInputInputs = forwardRef(
  (
    { onFocusChange, onChangeValue, value = conditionInputDefault, empty, onBlur, readOnly }: ConditionInputInputsProps,
    // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    _: Ref<void>
  ) => {
    const { isCategorySelected, operatorSetType, selectedRateConditionSubject, subject, operator, conditionValue } =
      useConditionInputLogic({ value, onChangeValue });

    const handleOpenChange = useCallback(
      (index: number) => (open: boolean) => onFocusChange(open ? index : -1),
      [onFocusChange]
    );

    return (
      <FullWidthGrid container direction="row">
        <SubjectGridItem size="grow">
          <ConditionSubjectSelect
            empty={empty}
            onBlur={onBlur}
            onChange={subject.onChange}
            onOpenChange={handleOpenChange(0)}
            readOnly={readOnly}
            value={subject.value}
          />
        </SubjectGridItem>
        {isCategorySelected && (
          <OperatorGridItem size="auto">
            <Suspense fallback={<ConditionInputPlaceholder />}>
              <ConditionOperatorSelect
                onBlur={onBlur}
                onChange={operator.onChange}
                onOpenChange={handleOpenChange(1)}
                operatorCategory={selectedRateConditionSubject?.conditionCategoryOperator}
                readOnly={readOnly}
                value={operator.value}
              />
            </Suspense>
          </OperatorGridItem>
        )}
        {isCategorySelected && (
          <ValueGridItem size="grow">
            <Suspense fallback={<ConditionInputPlaceholder />}>
              <ConditionValueInput
                category={value?.category ?? null}
                multiple={operatorSetType === "multiple"}
                onBlur={onBlur}
                onChange={conditionValue.onChange}
                onOpenChange={handleOpenChange(0)}
                readOnly={readOnly}
                subjectId={value?.subjectId ?? null}
                subjectType={value?.type ?? null}
                type={conditionValue.valueType}
                value={conditionValue.value}
              />
            </Suspense>
          </ValueGridItem>
        )}
      </FullWidthGrid>
    );
  }
);
ConditionInputInputs.displayName = "ConditionInputInputs";

/**
 * Provides the logic and properties required for a condition input handler, including operator, subject, and value management.
 *
 *
 * @returns An object containing key properties and handlers for managing condition inputs:
 *
 * Important Behavior:
 * - When the `operatorSetType` changes (e.g., from "single" to "multiple"), the logic adjusts the `value` to match the new set type.
 * - Updates to `valueInputProps` include changes in value representation (e.g., converting between single and array values).
 * - `onChangeValue` ensures changes propagate correctly while maintaining compatibility with the operator type.
 */
const useConditionInputLogic = ({
  value,
  onChangeValue,
}: Pick<ConditionInputInputsProps, "value" | "onChangeValue">) => {
  const selectedRateConditionSubject = useSelectedRateConditionSubject(value);
  const { getOperatorSetType } = useOperator();

  const operatorSetType = useMemo(
    () => (value?.operator ? getOperatorSetType(value?.operator as ConditionOperatorEnum) : null),
    [getOperatorSetType, value?.operator]
  );

  const isCategorySelected = (value?.subjectId ?? null) !== null || (value?.type ?? null) !== null;

  const handleChange: ConditionInputInputsProps["onChangeValue"] = useCallback(
    (val) => {
      const newOperatorSetType = val?.operator ? getOperatorSetType(val.operator as ConditionOperatorEnum) : null;
      if (newOperatorSetType !== operatorSetType && isNotNil(val) && isNotNil(operatorSetType)) {
        if (newOperatorSetType === "multiple" && operatorSetType === "single") {
          const newVal = ensureArray(val?.value ?? []).filter((element) => isNotNilOrEmpty(element));
          onChangeValue({ ...val, value: newVal });
        } else if (newOperatorSetType === "single" && operatorSetType === "multiple" && isArray(val?.value)) {
          onChangeValue({ ...val, value: head(val.value) ?? null });
        } else {
          onChangeValue(val);
        }
      } else {
        onChangeValue(val);
      }
    },
    [getOperatorSetType, onChangeValue, operatorSetType]
  );

  const subjectInputProps = useConditionSubjectInput(value, handleChange);

  const operatorInputProps = useConditionOperatorInput(value, handleChange);

  const valueInputProps = useConditionValueInput(value, handleChange);

  const valueType =
    operatorSetType === "none" || operatorSetType === null ? null : (selectedRateConditionSubject?.valueType ?? null);

  return {
    isCategorySelected,
    operatorSetType,
    selectedRateConditionSubject,
    subject: subjectInputProps,
    operator: operatorInputProps,
    conditionValue: {
      ...valueInputProps,
      valueType: valueType,
    },
  };
};

/**
 * A hook that generates a callback to modify a partial condition value.
 * It merges a default value, current value and a provided partial value deep into the input value.
 *
 * The resulting callback behavior:
 * 1. Merges a predefined default structure (`conditionInputDefault`) into the provided current value of condition
 * 2. Further merges the input `val` into the intermediate result from step 1.
 *
 * Key Notes:
 * - The merging behavior ensures that the final result always conforms to the default structure.
 */
const useModifyPartialConditionValue =
  (value: ConditionInputInputsProps["value"]) =>
  //useCallback(
  (val: DeepPartial<ConditionInputValue>): ConditionInputValue =>
    mergeDeepRight(mergeDeepRight(conditionInputDefault, value ?? {}))(val) as ConditionInputValue; //,
//[value]
//);
/**
 * Represents a custom hook to manage the subject-specific input state in a condition object.
 */
const useConditionSubjectInput = (
  value: ConditionInputInputsProps["value"],
  onChange: ConditionInputInputsProps["onChangeValue"]
) => {
  const modifyVal = useModifyPartialConditionValue(value);

  const subjectValue = useMemo(
    () => (value ? { category: value?.category ?? null, subjectId: value?.subjectId, type: value?.type } : null),
    [value]
  );
  const handleChangeSubject = useCallback<ConditionSubjectSelectProps["onChange"]>(
    (val) => onChange(isNull(val) ? null : modifyVal({ ...conditionInputDefault, ...val })),
    [onChange, modifyVal]
  );
  return {
    onChange: handleChangeSubject,
    value: subjectValue,
  };
};

/**
 * Represents a custom hook to manage the operator-specific input state in a condition object.
 */
const useConditionOperatorInput = (
  value: ConditionInputInputsProps["value"],
  onChange: ConditionInputInputsProps["onChangeValue"]
) => {
  const modifyVal = useModifyPartialConditionValue(value);

  const handleChangeOperator = useCallback<ConditionOperatorSelectProps["onChange"]>(
    (val) => onChange(modifyVal({ operator: val })),
    [onChange, modifyVal]
  );
  return {
    onChange: handleChangeOperator,
    value: value?.operator ?? null,
  };
};

/**
 Represents a custom hook to manage the value-specific input state in a condition object.
 */
const useConditionValueInput = (
  value: ConditionInputInputsProps["value"],
  onChange: ConditionInputInputsProps["onChangeValue"]
) => {
  const modifyVal = useModifyPartialConditionValue(value);

  const handleChangeValue = useCallback<ConditionValueInputProps["onChange"]>(
    (val) => onChange(modifyVal({ value: val })),
    [onChange, modifyVal]
  );
  return {
    onChange: handleChangeValue,
    value: value?.value ?? null,
  };
};

const ConditionInputPlaceholder = () => (
  <Skeleton sx={{ height: "100%", backgroundColor: "grey.300" }} variant="rectangular" />
);
